import { Component, effect, OnInit, PLATFORM_ID, inject, resource, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MatToolbar } from '@angular/material/toolbar';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { GoogleAuthService } from '../services/google-auth-service';
import { ThemeService } from '../services/theme.service';
import { UpdateService } from '../services/update.service';
import { MatTooltip } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FacebookBrowserDialog } from './facebook-browser-dialog';
import { detectInAppBrowser, detectIos } from '../services/geolocation.service';
import { ContactDialog } from './contact-dialog';
import { firstValueFrom } from 'rxjs';
import { UserService } from '../../../apiclients/playble';
import { MatBadge } from '@angular/material/badge';

import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';

@Component({
  selector: 'app-shell',
  imports: [
    MatToolbar,
    MatIconButton,
    MatIcon,
    MatProgressSpinner,
    RouterOutlet,
    RouterLink,
    MatTooltip,
    MatDialogModule,
    MatBadge,
    MatSidenavModule,
    MatListModule,
  ],
  templateUrl: './shell.html',
  styleUrl: './shell.css'
})
export class Shell implements OnInit {

  auth = inject(GoogleAuthService);
  router = inject(Router);
  themeService = inject(ThemeService);
  platformId = inject(PLATFORM_ID);
  dialog = inject(MatDialog);
  userService = inject(UserService);
  updateService = inject(UpdateService);

  facebookInAppBrowser = signal(false);
  facebookWarningDismissed = signal(false);
  restrictedInAppBrowserName = signal<string | null>(null);
  private readonly recordedVisitUsers = new Set<string>();

  adminQueueResource = resource({
    params: () => this.auth.currentUser()?.uid ?? null,
    loader: async ({ params }) => {
      if (!params) {
        return { isAdmin: false, pendingCount: 0, pendingPlaygroundCount: 0, pendingPlaygroupCount: 0, pendingDayTripCount: 0 };
      }

      try {
        const adminStatus = await firstValueFrom(this.userService.apiUserIsAdminGet());
        if (!adminStatus?.isAdmin) {
          return { isAdmin: false, pendingCount: 0, pendingPlaygroundCount: 0, pendingPlaygroupCount: 0, pendingDayTripCount: 0 };
        }

        return {
          isAdmin: true,
          pendingCount: adminStatus.pendingCount ?? 0,
          pendingPlaygroundCount: adminStatus.pendingPlaygroundCount ?? 0,
          pendingPlaygroupCount: adminStatus.pendingPlaygroupCount ?? 0,
          pendingDayTripCount: adminStatus.pendingDayTripCount ?? 0,
        };
      } catch {
        return { isAdmin: false, pendingCount: 0, pendingPlaygroundCount: 0, pendingPlaygroupCount: 0, pendingDayTripCount: 0 };
      }
    }
  });

  constructor() {
    effect(() => {
      const user = this.auth.currentUser();
      if (user?.uid) {
        void this.trackVisitOnce(user.uid);
      }
    });
  }

  async ngOnInit(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const hasDismissed = localStorage.getItem('facebookWarningDismissed') === 'true'
      || localStorage.getItem('playble.inAppBrowserWarningDismissed') === 'true';
    if (hasDismissed) {
      this.facebookWarningDismissed.set(true);
    }

    const userAgent = navigator.userAgent ?? '';
    const inAppName = detectInAppBrowser(userAgent);
    if (inAppName) {
      this.facebookInAppBrowser.set(true);
      this.restrictedInAppBrowserName.set(inAppName);
      if (!this.facebookWarningDismissed()) {
        const dialogRef = this.dialog.open(FacebookBrowserDialog, {
          autoFocus: false,
          data: {
            appName: inAppName,
            isIos: detectIos(userAgent, navigator.platform, navigator.maxTouchPoints),
          }
        });
        await firstValueFrom(dialogRef.afterClosed());
        this.dismissFacebookWarning();
      }
    }

    this.updateService.checkForUpdate();
  }

  private async trackVisitOnce(userId: string): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || this.recordedVisitUsers.has(userId)) {
      return;
    }

    this.recordedVisitUsers.add(userId);
    const key = `playble.visitRecorded.${userId}`;
    try {
      if (sessionStorage.getItem(key) === 'true') {
        return;
      }

      await firstValueFrom(this.userService.apiUserTrackVisitPost({ path: window.location.pathname }));
      sessionStorage.setItem(key, 'true');
    } catch {
      // Visit tracking is best-effort; never block the app for it.
    }
  }

  dismissFacebookWarning(): void {
    this.facebookWarningDismissed.set(true);
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    localStorage.setItem('facebookWarningDismissed', 'true');
    localStorage.setItem('playble.inAppBrowserWarningDismissed', 'true');
  }

  openContactDialog(event?: Event): void {
    event?.preventDefault();
    this.dialog.open(ContactDialog, {
      autoFocus: false,
      width: '560px',
      maxWidth: '95vw'
    });
  }

  isSignupRoute(): boolean {
    return this.router.url.startsWith('/signup');
  }

  isChromeHiddenRoute(): boolean {
    return this.isSignupRoute() || this.router.url.startsWith('/day-trips/new');
  }
}
